import React, { useState, useMemo, useCallback } from "react";
import {
  AccountStaff,
  LabStaff,
  Library,
  NonteachingStaff,
  TeachingStaff,
} from "../../../components/AcademicsActivities/Faculty/StaffDetals";
import FacultyCard, {
  ExecutiveLeaderCard,
  classifyStaffRole,
} from "../../../components/cards/Faculty";
import FacultyProfileModal from "../../../components/cards/FacultyProfileModal";
import HeaderBanner from "../../../components/HeaderBanner/index";
import director from "../../../assets/images/Dy-director.png";
import directoraa from "../../../assets/images/Director-AA.png";
import directordyaa from "../../../assets/images/Director.png";
import { FaSearch } from "@react-icons/all-files/fa/FaSearch";
import { FaCrown } from "@react-icons/all-files/fa/FaCrown";
import { FaGraduationCap } from "@react-icons/all-files/fa/FaGraduationCap";
import { FaUserCheck } from "@react-icons/all-files/fa/FaUserCheck";
import { FaBuilding } from "@react-icons/all-files/fa/FaBuilding";

// Top 3 Executive Leaders
const top3ExecutiveLeaders = [
  {
    Name: "S. Sukhbir Singh Chatha",
    Role: "Group Director - AA",
    ProfilePic: directoraa,
    Letter: "S",
  },
  {
    Name: "Dr. Ramandeep S. Deol",
    Role: "Director",
    ProfilePic: director,
    Letter: "R",
  },
  {
    Name: "Dr. S.K. Sood",
    Role: "Dy. Director - AA",
    ProfilePic: directordyaa,
    Letter: "S",
  },
];

const Faculty = () => {
  document.title = "Faculty & Leadership | LKCTC";
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewProfile = (member) => {
    setSelectedFaculty(member);
    setIsModalOpen(true);
  };

  // Search Filter Helper
  const filterMember = useCallback(
    (item) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase().trim();
      const nameStr = (item.Name || item.name || "").toLowerCase();
      const roleStr = (item.Role || item.Designation || "").toLowerCase();
      const deptStr = (item.Department || item.department || "").toLowerCase();
      const qualStr = (
        item.Qualification ||
        item.HighestQualification ||
        ""
      ).toLowerCase();

      return (
        nameStr.includes(term) ||
        roleStr.includes(term) ||
        deptStr.includes(term) ||
        qualStr.includes(term)
      );
    },
    [searchTerm],
  );

  // Categorize Teaching Staff into Deans & Asst Directors, HODs, and Faculty
  const { deansAndAsstDirectors, hodsList, generalFaculty } = useMemo(() => {
    const deans = [];
    const hods = [];
    const faculty = [];

    TeachingStaff.forEach((item) => {
      if (!filterMember(item)) return;
      const cat = classifyStaffRole(item.Role || item.Designation);

      if (cat === "executive") {
        // Executive leaders handled separately at top
        return;
      } else if (cat === "dean" || cat === "asst_director") {
        deans.push(item);
      } else if (cat === "hod") {
        hods.push(item);
      } else {
        faculty.push(item);
      }
    });

    return {
      deansAndAsstDirectors: deans,
      hodsList: hods,
      generalFaculty: faculty,
    };
  }, [filterMember]);

  // Filtered Support Staff
  const filteredLab = useMemo(() => {
    if (activeTab !== "all" && activeTab !== "support") return [];
    return LabStaff.filter(filterMember);
  }, [activeTab, filterMember]);

  const filteredNonTeaching = useMemo(() => {
    if (activeTab !== "all" && activeTab !== "support") return [];
    return NonteachingStaff.filter(filterMember);
  }, [activeTab, filterMember]);

  const filteredAccounts = useMemo(() => {
    if (activeTab !== "all" && activeTab !== "support") return [];
    return AccountStaff.filter(filterMember);
  }, [activeTab, filterMember]);

  const filteredLibrary = useMemo(() => {
    if (activeTab !== "all" && activeTab !== "support") return [];
    return Library.filter(filterMember);
  }, [activeTab, filterMember]);

  return (
    <div className="bg-slate-50/60 min-h-screen pb-20">
      {/* Header Banner */}
      <div>
        <HeaderBanner
          bgImage="Management"
          title="Faculty & Leadership"
          currentPage="Faculty"
        />
        <div className="bannerBotttom h-[3.5rem] w-full"></div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        {/* Directory Page Header & Search Bar */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-gray-200">
          <div>
            <div className="primary-color border-l-4 border-[#32727a] pl-4 text-3xl sm:text-4xl font-black">
              Faculty &amp;{" "}
              <span className="secondary-color">Leadership Directory</span>
            </div>
            <p className="mt-2 text-gray-600 text-sm sm:text-base pl-5">
              Explore our Executive Leadership, Deans, Heads of Departments, and
              Academic Faculty.
            </p>
          </div>

          {/* Search Box */}
          <div className="relative min-w-[280px] sm:min-w-[360px]">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
              <FaSearch className="w-4 h-4" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, department, role..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#32727a] focus:border-transparent shadow-sm transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs text-gray-400 hover:text-gray-600"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto py-6 scrollbar-hide">
          {[
            { id: "all", label: " All Members" },
            { id: "deans", label: " Deans & Directors" },
            { id: "hods", label: "Heads of Departments (HODs)" },
            { id: "faculty", label: "Teaching Faculty" },
            { id: "support", label: "Non-Teaching Support Staff" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-200 shadow-sm border ${
                activeTab === tab.id
                  ? "bg-[#081C3A] text-white border-[#081C3A] shadow-md scale-[1.02]"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* SECTION 1: TOP 3 EXECUTIVE LEADERSHIP (VIP HERO CARDS) */}
        {(activeTab === "all" || activeTab === "deans") && !searchTerm && (
          <div className="my-8">
            <div className="flex items-center gap-3 mb-6 pb-2 border-b border-amber-500/20">
              <div className="w-9 h-9 rounded-xl bg-amber-500/15 text-amber-700 flex items-center justify-center font-bold shadow-sm">
                <FaCrown className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-gray-900 tracking-tight">
                  Our <span className="text-amber-600">Leaders</span>
                </h2>
                <p className="text-xs text-gray-500">
                  Top Institutional Leadership
                </p>
              </div>
            </div>

            {/* 3-Column VIP Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
              {top3ExecutiveLeaders.map((leader, idx) => (
                <ExecutiveLeaderCard
                  key={idx}
                  Name={leader.Name}
                  Role={leader.Role}
                  ProfilePic={leader.ProfilePic}
                  Letter={leader.Letter}
                  onViewProfile={handleViewProfile}
                />
              ))}
            </div>
          </div>
        )}

        {/* SECTION 2: DEANS & ASSISTANT DIRECTORS */}
        {(activeTab === "all" || activeTab === "deans") &&
          deansAndAsstDirectors.length > 0 && (
            <div className="my-12">
              <div className="flex items-center justify-between mb-6 pb-2 border-b border-purple-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-800 flex items-center justify-center font-bold">
                    <FaUserCheck className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Deans &amp;{" "}
                      <span className="text-purple-700">
                        Assistant Directors
                      </span>
                    </h2>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {deansAndAsstDirectors.map((item, idx) => (
                  <FacultyCard
                    key={idx}
                    Name={item.Name || item.name}
                    Role={item.Role || item.Designation}
                    ProfilePic={item.ProfilePic}
                    Letter={item.Letter}
                    Department={item.Department || item.department}
                    Qualification={
                      item.Qualification || item.HighestQualification
                    }
                    Linkedin={item.Linkedin || item.LinkedinProfileLink}
                    Email={item.Email}
                    Specialisation={
                      item.Specialisation ||
                      item.Specialization ||
                      item.AreaOfInterest
                    }
                    ResearchPapers={item.ResearchPapers || item.Publications}
                    Bio={item.Bio || item.About}
                    onViewProfile={handleViewProfile}
                  />
                ))}
              </div>
            </div>
          )}

        {/* SECTION 3: HEADS OF DEPARTMENTS (HODs) */}
        {(activeTab === "all" || activeTab === "hods") &&
          hodsList.length > 0 && (
            <div className="my-12">
              <div className="flex items-center justify-between mb-6 pb-2 border-b border-teal-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-teal-100 text-teal-800 flex items-center justify-center font-bold">
                    <FaBuilding className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Heads of{" "}
                      <span className="text-[#32727a]">Departments (HODs)</span>
                    </h2>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {hodsList.map((item, idx) => (
                  <FacultyCard
                    key={idx}
                    Name={item.Name || item.name}
                    Role={item.Role || item.Designation}
                    ProfilePic={item.ProfilePic}
                    Letter={item.Letter}
                    Department={item.Department || item.department}
                    Qualification={
                      item.Qualification || item.HighestQualification
                    }
                    Linkedin={item.Linkedin || item.LinkedinProfileLink}
                    Email={item.Email}
                    Specialisation={
                      item.Specialisation ||
                      item.Specialization ||
                      item.AreaOfInterest
                    }
                    ResearchPapers={item.ResearchPapers || item.Publications}
                    Bio={item.Bio || item.About}
                    onViewProfile={handleViewProfile}
                  />
                ))}
              </div>
            </div>
          )}

        {/* SECTION 4: TEACHING FACULTY */}
        {(activeTab === "all" || activeTab === "faculty") &&
          generalFaculty.length > 0 && (
            <div className="my-12">
              <div className="flex items-center justify-between mb-6 pb-2 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#081C3A] text-white flex items-center justify-center font-bold">
                    <FaGraduationCap className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Academic{" "}
                      <span className="text-[#32727a]">Teaching Faculty</span>
                    </h2>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {generalFaculty.map((item, idx) => (
                  <FacultyCard
                    key={idx}
                    Name={item.Name || item.name}
                    Role={item.Role || item.Designation}
                    ProfilePic={item.ProfilePic}
                    Letter={item.Letter}
                    Department={item.Department || item.department}
                    Qualification={
                      item.Qualification || item.HighestQualification
                    }
                    Linkedin={item.Linkedin || item.LinkedinProfileLink}
                    Email={item.Email}
                    Specialisation={
                      item.Specialisation ||
                      item.Specialization ||
                      item.AreaOfInterest
                    }
                    ResearchPapers={item.ResearchPapers || item.Publications}
                    Bio={item.Bio || item.About}
                    onViewProfile={handleViewProfile}
                  />
                ))}
              </div>
            </div>
          )}

        {/* SECTION 5: NON-TEACHING SUPPORT STAFF */}
        {(activeTab === "all" || activeTab === "support") && (
          <>
            {/* Lab Staff */}
            {filteredLab.length > 0 && (
              <div className="my-12">
                <div className="flex items-center justify-between mb-6 pb-2 border-b border-amber-200">
                  <h3 className="text-xl font-bold text-gray-800">
                    Lab <span className="text-amber-600">Staff</span>
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredLab.map((item, idx) => (
                    <FacultyCard
                      key={idx}
                      Name={item.Name}
                      Role={item.Role}
                      Letter={item.Letter}
                      ProfilePic={item.ProfilePic}
                      badgeLabel="LAB STAFF"
                      onViewProfile={handleViewProfile}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Non-Teaching / Administration Staff */}
            {filteredNonTeaching.length > 0 && (
              <div className="my-12">
                <div className="flex items-center justify-between mb-6 pb-2 border-b border-amber-200">
                  <h3 className="text-xl font-bold text-gray-800">
                    Administration <span className="text-amber-600">Staff</span>
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredNonTeaching.map((item, idx) => (
                    <FacultyCard
                      key={idx}
                      Name={item.Name}
                      Role={item.Role}
                      Letter={item.Letter}
                      ProfilePic={item.ProfilePic}
                      badgeLabel="ADMIN STAFF"
                      onViewProfile={handleViewProfile}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Accounts Staff */}
            {filteredAccounts.length > 0 && (
              <div className="my-12">
                <div className="flex items-center justify-between mb-6 pb-2 border-b border-amber-200">
                  <h3 className="text-xl font-bold text-gray-800">
                    Accounts <span className="text-amber-600">Department</span>
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredAccounts.map((item, idx) => (
                    <FacultyCard
                      key={idx}
                      Name={item.Name}
                      Role={item.Role}
                      Letter={item.Letter}
                      ProfilePic={item.ProfilePic}
                      badgeLabel="ACCOUNTS STAFF"
                      onViewProfile={handleViewProfile}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Library Staff */}
            {filteredLibrary.length > 0 && (
              <div className="my-12">
                <div className="flex items-center justify-between mb-6 pb-2 border-b border-amber-200">
                  <h3 className="text-xl font-bold text-gray-800">
                    Library <span className="text-amber-600">Department</span>
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredLibrary.map((item, idx) => (
                    <FacultyCard
                      key={idx}
                      Name={item.Name}
                      Role={item.Role}
                      Letter={item.Letter}
                      ProfilePic={item.ProfilePic}
                      badgeLabel="LIBRARY STAFF"
                      onViewProfile={handleViewProfile}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Faculty Profile Details Modal */}
      <FacultyProfileModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        faculty={selectedFaculty}
      />
    </div>
  );
};

export default Faculty;
